'use client';

import { useState, useEffect, useCallback } from 'react';
import { logger } from '@/lib/logger';
import { supabase } from '@/lib/supabase/client';
import type { Factor, AuthenticatorAssuranceLevels } from '@supabase/supabase-js';
import { trackEvent } from '@/lib/posthog';
import { errorEvents } from '@/lib/analytics/events/errors';

export type MFAStatus = 'loading' | 'unenrolled' | 'unverified' | 'verified';

export interface MFAState {
  status: MFAStatus;
  currentLevel: 'aal1' | 'aal2';
  nextLevel: 'aal1' | 'aal2';
  factors: Factor[];
  verifiedFactors: Factor[];
}

export interface EnrollmentData {
  factorId: string;
  qrCode: string;
  secret: string;
  uri: string;
}

export function useMFA() {
  const [mfaState, setMfaState] = useState<MFAState>({
    status: 'loading',
    currentLevel: 'aal1',
    nextLevel: 'aal1',
    factors: [],
    verifiedFactors: [],
  });
  const [isLoading, setIsLoading] = useState(true);

  // Check MFA status
  const checkMFAStatus = useCallback(async () => {
    setIsLoading(true);
    try {
      // `listFactors()` reads the factors cached on the local session, not the server. A session issued before
      // a factor was enrolled therefore reports "no factors" forever, and the caller sends an already-enrolled
      // user to ENROLMENT instead of to verification — which is how a long-lived session produced "Factor not
      // found" on the advisor accept page (#1878). Ask the server who this user is first.
      await supabase.auth.getUser().catch(() => undefined);
      const [aalResult, factorsResult] = await Promise.all([
        supabase.auth.mfa.getAuthenticatorAssuranceLevel(),
        supabase.auth.mfa.listFactors(),
      ]);

      if (aalResult.error) {
        logger.error('[MFA] Error getting AAL:', aalResult.error);
        const errorEvent = errorEvents.authError('mfa_get_aal', aalResult.error.message);
        trackEvent(errorEvent.event, errorEvent.properties);
        setMfaState(prev => ({ ...prev, status: 'unenrolled' }));
        return;
      }

      const { currentLevel, nextLevel } = aalResult.data;
      const factors = factorsResult.data?.totp ?? [];
      const verifiedFactors = factors.filter(f => f.status === 'verified');

      let status: MFAStatus;
      if (verifiedFactors.length === 0) {
        status = 'unenrolled';
      } else if (currentLevel === 'aal2') {
        status = 'verified';
      } else if (nextLevel === 'aal2') {
        status = 'unverified';
      } else {
        // A verified factor exists but the session has not caught up. "Unenrolled" would send this person to
        // set up an authenticator they already have; "unverified" sends them to the code prompt, which is the
        // step they actually owe (#1878).
        status = 'unverified';
      }

      setMfaState({
        status,
        currentLevel: currentLevel as 'aal1' | 'aal2',
        nextLevel: nextLevel as 'aal1' | 'aal2',
        factors,
        verifiedFactors,
      });
    } catch (error) {
      logger.error('[MFA] Error checking status:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      const errorEvent = errorEvents.authError('mfa_check_status', errorMessage);
      trackEvent(errorEvent.event, errorEvent.properties);
      setMfaState(prev => ({ ...prev, status: 'unenrolled' }));
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial check
  useEffect(() => {
    checkMFAStatus();
  }, [checkMFAStatus]);

  // Start enrollment - returns QR code and secret
  // IMPORTANT: Uses unique friendly names to prevent "factor already exists" errors
  // Also handles cleanup of unverified factors when possible
  const startEnrollment = async (friendlyName: string = 'Authenticator App', retryCount: number = 0): Promise<{
    data: EnrollmentData | null;
    error: Error | null;
  }> => {
    try {
      // Generate unique friendly name with timestamp to prevent collisions
      const uniqueFriendlyName = retryCount === 0
        ? `${friendlyName} ${Date.now()}`
        : `${friendlyName} ${Date.now()}-${retryCount}`;

      logger.log('[MFA] Starting enrollment with friendlyName:', uniqueFriendlyName);

      // Try to clean up any existing unverified factors (best effort - may fail with 422)
      try {
        const { data: existingFactors, error: listError } = await (supabase as any).auth.mfa.listFactors();

        if (listError) {
          // 422 error is common right after authentication - log and continue
          logger.warn('[MFA] listFactors failed (this is OK, continuing):', listError.message);
        } else if (existingFactors?.totp) {
          const unverifiedFactors = existingFactors.totp.filter((f: Factor) => f.status === 'unverified');
          logger.log('[MFA] Found unverified factors:', unverifiedFactors.length);

          // Unenroll all unverified factors before creating a new one
          for (const factor of unverifiedFactors) {
            logger.log('[MFA] Unenrolling unverified factor:', factor.id, factor.friendly_name);
            try {
              await (supabase as any).auth.mfa.unenroll({ factorId: factor.id });
            } catch (unenrollErr) {
              logger.warn('[MFA] Failed to unenroll factor (continuing):', unenrollErr);
            }
          }
        }
      } catch (cleanupErr) {
        logger.warn('[MFA] Factor cleanup failed (continuing anyway):', cleanupErr);
      }

      // Now enroll with a fresh factor using unique name
      const { data, error } = await (supabase as any).auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: uniqueFriendlyName,
      });

      if (error) {
        logger.error('[MFA] Enrollment error:', error);

        // If "already exists" error and we haven't retried too many times, retry with new name
        if (error.message?.includes('already exists') && retryCount < 3) {
          logger.log('[MFA] Factor name collision, retrying with new unique name...');
          // Small delay before retry
          await new Promise(resolve => setTimeout(resolve, 500));
          return startEnrollment(friendlyName, retryCount + 1);
        }

        return { data: null, error };
      }

      logger.log('[MFA] Enrollment successful, factor ID:', data.id);
      return {
        data: {
          factorId: data.id,
          qrCode: data.totp.qr_code,
          secret: data.totp.secret,
          uri: data.totp.uri,
        },
        error: null,
      };
    } catch (error) {
      logger.error('[MFA] Enrollment exception:', error);
      return { data: null, error: error as Error };
    }
  };

  // Verify enrollment with TOTP code
  const verifyEnrollment = async (factorId: string, code: string): Promise<{
    success: boolean;
    error: Error | null;
  }> => {
    try {
      const { error } = await (supabase as any).auth.mfa.challengeAndVerify({
        factorId,
        code,
      });

      if (error) {
        return { success: false, error };
      }

      // Refresh MFA status
      await checkMFAStatus();

      return { success: true, error: null };
    } catch (error) {
      return { success: false, error: error as Error };
    }
  };

  // Verify MFA challenge (for login)
  const verifyChallenge = async (code: string): Promise<{
    success: boolean;
    error: Error | null;
  }> => {
    try {
      // Get the first verified TOTP factor
      const { data: factors, error: factorsError } = await (supabase as any).auth.mfa.listFactors();

      if (factorsError) {
        return { success: false, error: factorsError };
      }

      const totpFactor = factors.totp.find((f: Factor) => f.status === 'verified');
      if (!totpFactor) {
        return { success: false, error: new Error('No verified TOTP factor found') };
      }

      const { error } = await (supabase as any).auth.mfa.challengeAndVerify({
        factorId: totpFactor.id,
        code,
      });

      if (error) {
        return { success: false, error };
      }

      // Refresh MFA status
      await checkMFAStatus();

      return { success: true, error: null };
    } catch (error) {
      return { success: false, error: error as Error };
    }
  };

  // Unenroll a factor
  const unenrollFactor = async (factorId: string): Promise<{
    success: boolean;
    error: Error | null;
  }> => {
    try {
      const { error } = await (supabase as any).auth.mfa.unenroll({ factorId });

      if (error) {
        return { success: false, error };
      }

      // Refresh MFA status
      await checkMFAStatus();

      return { success: true, error: null };
    } catch (error) {
      return { success: false, error: error as Error };
    }
  };

  return {
    ...mfaState,
    isLoading,
    checkMFAStatus,
    startEnrollment,
    verifyEnrollment,
    verifyChallenge,
    unenrollFactor,
    // Computed helpers
    requiresVerification: mfaState.status === 'unverified',
    hasEnrolledMFA: mfaState.verifiedFactors.length > 0,
    isAtAAL2: mfaState.currentLevel === 'aal2',
  };
}
